import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { speakingEngagements } from '@/mocks/home';
import { pressArticles } from '@/mocks/press';

const publicationIcons: Record<string, string> = {
  'Voyage Houston': 'ri-compass-line',
  'Shoutout HTX': 'ri-star-line',
  'CanvasRebel': 'ri-palette-line',
  'From $0 to $500K': 'ri-book-open-line',
  'Podcast Feature': 'ri-mic-2-line',
  'Regional Hispanic Contractors Association': 'ri-shield-check-line',
};

const publications = [
  { name: 'SHRM', icon: 'ri-shield-user-line' },
  { name: 'HR Houston', icon: 'ri-group-line' },
  { name: 'Gulf Coast Symposium', icon: 'ri-landscape-line' },
  { name: 'Regional Hispanic Contractors Association', icon: 'ri-shield-check-line' },
  { name: 'Voyage Houston', icon: 'ri-compass-line' },
  { name: 'Shoutout HTX', icon: 'ri-star-line' },
  { name: 'CanvasRebel', icon: 'ri-palette-line' },
];

export default function SpeakingMediaSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const featuredPress = pressArticles.slice(0, 3);

  return (
    <section ref={ref} className="bg-charcoal py-14 md:py-20 lg:py-28 border-t border-white/5">
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto">

          {/* Section Header */}
          <div className="mb-10 md:mb-14">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
              <div>
                <p
                  className={`text-gold text-[10px] sm:text-xs font-semibold tracking-[0.25em] uppercase mb-2 sm:mb-3 transition-all duration-700 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  Speaking &amp; Media
                </p>
                <h2
                  className={`text-white text-xl sm:text-2xl md:text-3xl lg:text-display-sm font-bold leading-tight transition-all duration-700 delay-100 ${
                    visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                >
                  Leadership &amp;<br className="hidden sm:block" />{' '}
                  <span className="sm:hidden"> </span>Industry Engagement
                </h2>
              </div>
              <Link
                to="/speaking"
                className={`inline-flex items-center gap-1.5 text-gold font-semibold text-xs sm:text-sm hover:text-gold-light transition-all duration-700 delay-200 whitespace-nowrap self-start sm:self-auto flex-shrink-0 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                View All Engagements &amp; Press
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 lg:gap-14">

            {/* LEFT — Speaking Engagements Timeline */}
            <div>
              <p
                className={`text-white/30 text-[10px] font-semibold tracking-[0.2em] uppercase mb-5 sm:mb-6 transition-all duration-700 delay-150 ${
                  visible ? 'opacity-100' : 'opacity-0'
                }`}
              >
                Engagements
              </p>
              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-px bg-gold/25"></div>
                <div className="space-y-5 sm:space-y-6">
                  {speakingEngagements.map((event, index) => (
                    <div
                      key={event.id}
                      className={`relative pl-9 sm:pl-10 transition-all duration-700 ${
                        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                      }`}
                      style={{ transitionDelay: `${250 + index * 150}ms` }}
                    >
                      {/* Timeline dot */}
                      <div className="absolute left-1 top-1.5 w-4 h-4 rounded-full bg-gold border-4 border-charcoal"></div>

                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-gold font-bold text-xs">{event.year}</span>
                      </div>

                      <div className="bg-dark-card rounded-xl p-4 sm:p-5 border border-white/5 hover:border-gold/20 transition-colors">
                        <h3 className="text-white font-semibold text-sm sm:text-base mb-1 leading-snug">
                          {event.event}
                        </h3>
                        <p className="text-gold text-xs font-medium mb-2 leading-snug">{event.topic}</p>
                        <p className="text-white/40 text-xs leading-relaxed">
                          {event.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT — Press & Media */}
            <div className="mt-2 lg:mt-0">
              <p
                className={`text-white/30 text-[10px] font-semibold tracking-[0.2em] uppercase mb-5 sm:mb-6 transition-all duration-700 delay-150 ${
                  visible ? 'opacity-100' : 'opacity-0'
                }`}
              >
                Press &amp; Media
              </p>

              {/* Publication logos strip */}
              <div
                className={`flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-7 transition-all duration-700 delay-200 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                {publications.map((pub) => (
                  <div key={pub.name} className="flex items-center gap-1.5 group">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-md bg-white/[0.04] group-hover:bg-gold/10 transition-colors">
                      <i className={`${pub.icon} text-white/30 group-hover:text-gold text-xs sm:text-sm transition-colors`}></i>
                    </div>
                    <span className="text-white/30 text-[11px] sm:text-xs font-medium group-hover:text-white/50 transition-colors whitespace-nowrap">
                      {pub.name}
                    </span>
                  </div>
                ))}
              </div>

              {/* Press articles */}
              <div className="space-y-3 sm:space-y-4">
                {featuredPress.map((article, index) => {
                  const icon = publicationIcons[article.publication] || 'ri-article-line';
                  return (
                    <div
                      key={article.id}
                      className={`group bg-dark-card rounded-xl p-3.5 sm:p-4 md:p-5 border border-white/5 hover:border-gold/20 transition-all duration-500 ${
                        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                      }`}
                      style={{ transitionDelay: `${300 + index * 120}ms` }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-gold/10 group-hover:bg-gold/20 transition-colors">
                          <i className={`${icon} text-gold text-sm sm:text-base`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 sm:mb-1.5 flex-wrap">
                            <span className="text-gold text-[10px] font-semibold tracking-wide uppercase">
                              {article.publication}
                            </span>
                            {article.year && (
                              <span className="text-white/20 text-[10px]">{article.year}</span>
                            )}
                          </div>
                          <h3 className="text-white font-semibold text-xs sm:text-sm mb-1 group-hover:text-gold-light transition-colors leading-snug">
                            {article.title}
                          </h3>
                          <p className="text-white/35 text-xs leading-relaxed line-clamp-2">
                            {article.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className={`mt-4 sm:mt-5 transition-all duration-700 delay-600 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <Link
                  to="/press-media"
                  className="inline-flex items-center gap-1.5 text-white/30 text-xs font-medium hover:text-gold transition-colors"
                >
                  View all press &amp; media
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}